'use strict';

const {
	totalGetter, countGetter, avgGetter, ccuGetter,
	formatter, minutesFormatter, sessionLengthFormatter,
	sparkLine, reduceTotal, getRow, isStale, SelfReloadingController
} = require("./shared");

const _ = require('lodash');
const Controller = require('../../shared/controller');
const tables = require('../../components/tables');
const config = require('../../shared/config');
const {identity, pretty} = require('../../shared/utils');

const METRICS_BLOCK_TEMPLATE = require('../../templates/metrics-block.html');
const METRICS_BLOCK_COLUMNS = [
	{key: 'title', title: 'Filter', formatter: identity},
	{
		title: '', sort: false,
		call: function (td) {
			td.each(sparkLine);
		}
	},
	{key: 'h1', title: 'Hour', reduceTotal},
	{key: 'h24', title: '24 hours', order: 'desc', reduceTotal},
	{key: 'day7', title: '7 days', reduceTotal},
	{key: 'day30', title: '30 days', reduceTotal},
	{key: 'year', title: 'Year', reduceTotal},
	{key: 'life', title: 'Lifetime', reduceTotal}
];

class MetricsBlock extends Controller {
	constructor(options) {
		super(Object.assign({formatter, template: METRICS_BLOCK_TEMPLATE}, options));
	}

	get columns() {
		// Add sum function if provided
		return METRICS_BLOCK_COLUMNS;
	}

	get table() {
		return this._table;
	}

	getRenderOptions(options) {
		options = super.getRenderOptions(options);
		let data = options.data;
		let rows = [];
		let filters = Object.keys(data);
		filters.sort();
		let stale = false;
		for (let f of filters) {
			if (!f) {
				// TODO: Has to be fixed on API / Processing side.
				continue;
			}
			rows.push(getRow(data[f], f, options.getter));
			stale = stale || isStale(data[f]);
		}
		options.tableData = rows;
		options.stale = stale;
		return options;
	}

	drawTable(dom, data) {
		let TableClass;
		if (this.options.showTotal) {
			TableClass = tables.TotalsTable;
		} else {
			TableClass = tables.SortableTable;
		}
		let table = this._table = new TableClass(dom, Object.assign({
			columns: this.columns,
			css: 'cm-metrics-table cm-table table table-stripped'
		}, _.pick(this.options, 'dateFormat', 'numberFormat', 'formatter')));
		table.draw(data);
	}

	draw(options) {
		options = super.draw(options);
		let container = this.container.querySelector('table');
		container.id = _.uniqueId();
		this.drawTable(container, options.tableData);
		return options;
	}

	redraw(options) {
		this._table.redraw(options.tableData);
		return options;
	}
}

const GROUPED_METRICS_TAB_TEMPLATE = require('../../templates/dashboard-grouped-metrics-tab.html');

class MetricsByFilter extends SelfReloadingController {
	constructor(options) {
		super({
			template: GROUPED_METRICS_TAB_TEMPLATE,
			method: 'dashboard/metrics-by-filter',
			filter: 'Car'
		}, options);
		this._metrics = new Map();
		this._metrics.set('minutes', new MetricsBlock({
			title: 'Minutes used', container: '.cm-minutes-metrics', showTotal: true,
			getter: totalGetter, formatter: minutesFormatter
		}));
		this._metrics.set('streams', new MetricsBlock({
			title: 'Active streams', container: '.cm-stream-metrics', showTotal: true,
			getter: countGetter
		}));
		this._metrics.set('sessions', new MetricsBlock({
			title: 'Average Session Length (minutes)', container: '.cm-sessions-metrics', showTotal: false,
			getter: avgGetter, formatter: sessionLengthFormatter
		}));
		this._metrics.set('ccu', new MetricsBlock({
			title: 'Peak CCU', container: '.cm-peak-metrics', showTotal: false,
			getter: ccuGetter
		}));
	}

	getApiCallOptions(options) {
		options = super.getApiCallOptions(options);
		options.params = Object.assign({groupBy: options.filter}, options.params);
		return options;
	}

	processResponse(response) {
		return response && response.data;
	}

	render(options) {
		let self = this;
		return super.render(options)
			.then(function (options) {
				let c = self.container;
				let tasks = [];
				for (let monitor of self._metrics.values()) {
					let mc = c.querySelector(monitor.options.container);
					if (mc) {
						tasks.push(monitor.render({container: mc, data: options.data}));
					}
				}
				return Promise.all(tasks)
					.then(function (results) {
						options.stale = false;
						for (let r of results) {
							if (r.stale) {
								options.stale = true;
								break;
							}
						}
						// console.debug('MetricsBlock.render', options.stale);
						return options;
					});
			});
	}

	update(options) {
		let tasks = [];
		for (let monitor of this._metrics.values()) {
			tasks.push(monitor.update({data: options.data}));
		}
		return Promise.all(tasks)
			.then(function (results) {
				let o = Object.assign({}, options);
				o.stale = false;
				for (let r of results) {
					if (r.stale) {
						o.stale = true;
						break;
					}
				}
				return o;
			});
	}
}

const GROUPED_METRICS_TEMPLATE = require('../../templates/dashboard-grouped-metrics.html');
class FiltersSwitch extends Controller {
	constructor(options) {
		super({template: GROUPED_METRICS_TEMPLATE}, options);
		options = this.options;
		let groupBy = options.groupBy || config.get('GroupDashboardMetricsBy', 'Car');
		this._tabs = new Map();
		this._groupByOptions = new Map();
		this._selected = null;
		this._params = options.params || {};
		for (let f of groupBy) {
			let filter, title;
			if (Array.isArray(f)) {
				([title, filter] = f);
			} else {
				filter = title = f;
			}
			this._selected = this._selected || filter;
			this._tabs.set(filter, new MetricsByFilter({filter, title}));
			this._groupByOptions.set(filter, {filter, title});
		}
	}

	getRenderOptions(options) {
		options = super.getRenderOptions(options);
		options.groupBy = [...this._groupByOptions.values()];
		options.selected = this._groupByOptions.get(this._selected);
		return options;
	}

	render(options) {
		let self = this;
		return super.render(options)
			.then(function (options) {
				self.initPills();
				return self.showTab(options.params)
					.then(function () { return options; });
			})
	}

	update(options) {
		return this.showTab(options.params)
			.then(function () { return options; });
	}

	initPills() {
		let tabsContainer = this.container.querySelector('#filters-tabs');
		if (!tabsContainer) {
			// Sometimes we don't show pills
			return;
		}
		for (let filter of this._tabs.keys()) {
			let tab = tabsContainer.querySelector(`li[data-filter=${filter}]`);
			if (filter === this._selected) {
				tab.classList.add('active');
			} else {
				tab.classList.remove('active');
			}
			let self = this;
			tab.querySelector('a').addEventListener('click', function (e) {
				e.preventDefault();
				e.stopImmediatePropagation();
				self._selected = this.getAttribute('data-filter');
				self.updatePills();
				self.showTab()
					.then(function () {
						try {
							self.container.scrollIntoView();
						} catch (e) {}
					});
			});
		}
	}

	updatePills() {
		let tabsContainer = this.container.querySelector('#filters-tabs');
		for (let filter of this._tabs.keys()) {
			let tab = tabsContainer.querySelector(`li[data-filter=${filter}]`);
			if (filter === this._selected) {
				tab.classList.add('active');
			} else {
				tab.classList.remove('active');
			}
		}
	}

	showTab(params) {
		let tabController = this._tabs.get(this._selected);
		params = params || this._params;
		let tabOptions = {params};
		let container = tabController.container;
		if (!container) {
			container = document.createElement('div');
			tabOptions.container = container;
		}
		let tabSection = this.container.querySelector('#metrics');
		tabSection.innerHTML = '';
		tabSection.appendChild(container);
		let header = this.container.querySelector('.page-header small');
		if (header) {
			let selectedTitle = this._groupByOptions.get(this._selected).title;
			header.innerHTML = `by ${pretty(selectedTitle)}`;
		}
		let self = this;
		return tabController.load(tabOptions)
			.then(function () {
				self._params = params;
			});
	}
}


module.exports = FiltersSwitch;